import { canPayResources, getResourceShortfall } from "../resources/resource-pool";
import {
  getArmyDefinitionV3ForInstance,
  getCardInstance,
  getCardInstanceDefinition,
  isSetupGameState,
} from "../selectors/card-instances";
import { getGarrisonSnapshot, MAX_ARMY_CARDS_PER_LANE } from "../selectors/garrison";
import type {
  ActionValidationFailure,
  ActionValidationResult,
  DeployArmyAction,
  GameState,
  ResourcePool,
} from "../types";

function invalid(
  code: ActionValidationFailure["code"],
  message: string,
  path: string | null = null,
  details: ActionValidationFailure["details"] = {},
): ActionValidationResult {
  return { valid: false, failure: { code, message, path, details } };
}

function resourceDetails(required: ResourcePool, available: ResourcePool) {
  const shortfall = getResourceShortfall(available, required);
  return {
    requiredFood: required.food,
    requiredProduction: required.production,
    requiredTrade: required.trade,
    requiredScience: required.science,
    requiredCulture: required.culture,
    availableFood: available.food,
    availableProduction: available.production,
    availableTrade: available.trade,
    availableScience: available.science,
    availableCulture: available.culture,
    shortfallFood: shortfall.food,
    shortfallProduction: shortfall.production,
    shortfallTrade: shortfall.trade,
    shortfallScience: shortfall.science,
    shortfallCulture: shortfall.culture,
  } as const;
}

export function validateDeployArmyAction(
  state: Readonly<GameState>,
  action: DeployArmyAction,
): ActionValidationResult {
  if (
    (state.schemaVersion !== 3 && state.schemaVersion !== 4 && state.schemaVersion !== 5) ||
    (state.catalog.schemaVersion !== 3 && state.catalog.schemaVersion !== 4)
  ) {
    return invalid(
      "ACTION_NOT_IMPLEMENTED",
      "Army deployment requires gameplay-catalog schema 3.",
      "catalog.schemaVersion",
      {
        actionKind: "deployment-action",
        stateSchemaVersion: state.schemaVersion,
        catalogSchemaVersion: isSetupGameState(state) ? state.catalog.schemaVersion : null,
        requiredCatalogSchemaVersion: state.schemaVersion === 5 ? 4 : 3,
      },
    );
  }

  if (
    state.phase !== "command" &&
    !(state.schemaVersion === 5 && state.phase === "action-window")
  ) {
    return invalid(
      "WRONG_PHASE",
      "An Army may be deployed only during the Command Phase.",
      "phase",
      {
        currentPhase: state.phase,
        requiredPhase: state.schemaVersion === 5 ? "action-window" : "command",
      },
    );
  }

  const player = state.players[action.playerId];
  if (player.actionsUsed.deploymentActions >= 1) {
    return invalid(
      "DEPLOYMENT_ACTION_ALREADY_USED",
      "A player may use only one Deployment Action per turn.",
      `players.${action.playerId}.actionsUsed.deploymentActions`,
      { deploymentActionsUsed: player.actionsUsed.deploymentActions },
    );
  }

  const instance = getCardInstance(state, action.armyCardInstanceId);
  if (instance === undefined) {
    return invalid(
      "CARD_INSTANCE_NOT_FOUND",
      "The selected Army card instance does not exist in this match.",
      "armyCardInstanceId",
      { armyCardInstanceId: action.armyCardInstanceId },
    );
  }

  if (instance.ownerPlayerId !== action.playerId) {
    return invalid(
      "CARD_NOT_OWNED_BY_PLAYER",
      "A player cannot deploy another player's Army.",
      `cardInstances.${action.armyCardInstanceId}.ownerPlayerId`,
      { playerId: action.playerId, armyCardInstanceId: action.armyCardInstanceId },
    );
  }

  if (!player.hand.includes(action.armyCardInstanceId)) {
    return invalid(
      "CARD_NOT_IN_HAND",
      "The incoming Army must be in the acting player's hand.",
      `players.${action.playerId}.hand`,
      { armyCardInstanceId: action.armyCardInstanceId },
    );
  }

  const definition = getCardInstanceDefinition(state, action.armyCardInstanceId);
  if (definition === undefined) {
    return invalid(
      "CARD_DEFINITION_NOT_FOUND",
      "The selected Army card instance references an unknown definition.",
      `cardInstances.${action.armyCardInstanceId}.cardDefinitionId`,
      { cardDefinitionId: instance.cardDefinitionId },
    );
  }

  if (definition.type !== "army") {
    return invalid(
      "CARD_NOT_ARMY",
      "Only an Army card may be deployed with this action.",
      "armyCardInstanceId",
      {
        armyCardInstanceId: action.armyCardInstanceId,
        cardDefinitionId: definition.id,
        actualCardType: definition.type,
      },
    );
  }

  const armyDefinition = getArmyDefinitionV3ForInstance(state, action.armyCardInstanceId);
  if (armyDefinition === undefined) {
    return invalid(
      "CARD_DEFINITION_NOT_FOUND",
      "The selected Army lacks the schema-3 printed gameplay projection.",
      `catalog.cards.${definition.id}`,
      { cardDefinitionId: definition.id, requiredCatalogSchemaVersion: 3 },
    );
  }

  const lane = state.lanes[action.laneId];
  if (lane === undefined || lane.result !== "active") {
    return invalid(
      "TARGET_LANE_NOT_ACTIVE",
      "An Army cannot be deployed into a resolved or unknown lane.",
      `lanes.${action.laneId}`,
      {
        laneId: action.laneId,
        laneResult: lane?.result ?? null,
      },
    );
  }

  const side = lane.players[action.playerId];
  if (side.status !== "active") {
    return invalid(
      side.status === "empty" ? "TARGET_LANE_HAS_NO_LAND" : "TARGET_LANE_NOT_ACTIVE",
      "An Army may be deployed only onto the acting player's active Land.",
      `lanes.${action.laneId}.players.${action.playerId}.status`,
      { laneId: action.laneId, laneSideStatus: side.status },
    );
  }

  if (side.land === null) {
    return invalid(
      "TARGET_LANE_HAS_NO_LAND",
      "The target lane has no active Land for this Army.",
      `lanes.${action.laneId}.players.${action.playerId}.land`,
      { laneId: action.laneId },
    );
  }

  const commander = side.commander;
  if (state.schemaVersion !== 5 && commander === null) {
    return invalid(
      "TARGET_LANE_HAS_NO_COMMANDER",
      "An Army may be deployed only onto a Land containing a Commander.",
      `lanes.${action.laneId}.players.${action.playerId}.commander`,
      { laneId: action.laneId },
    );
  }

  const garrison = getGarrisonSnapshot(state, action.playerId, action.laneId);
  if (garrison.armyCount >= MAX_ARMY_CARDS_PER_LANE) {
    return invalid(
      "ARMY_CARD_LIMIT_REACHED",
      "A lane may contain no more than five Army cards.",
      `lanes.${action.laneId}.players.${action.playerId}.armies`,
      {
        laneId: action.laneId,
        currentArmyCount: garrison.armyCount,
        maximumArmyCount: MAX_ARMY_CARDS_PER_LANE,
      },
    );
  }

  const totalArmySizeAfter = garrison.totalArmySize + armyDefinition.armySize;
  if (
    state.schemaVersion !== 5 &&
    commander !== null &&
    commander.armyCapacity !== undefined &&
    totalArmySizeAfter > commander.armyCapacity
  ) {
    return invalid(
      "COMMANDER_CAPACITY_EXCEEDED",
      "The incoming Army would exceed the Commander's Army Capacity.",
      `lanes.${action.laneId}.players.${action.playerId}.armies`,
      {
        laneId: action.laneId,
        currentArmySize: garrison.totalArmySize,
        incomingArmySize: armyDefinition.armySize,
        totalArmySizeAfter,
        commanderArmyCapacity: commander.armyCapacity,
      },
    );
  }

  if (!canPayResources(player.kingdomPool, armyDefinition.cost)) {
    return invalid(
      "INSUFFICIENT_KINGDOM_POOL",
      "The Kingdom Pool cannot pay the incoming Army's exact printed cost.",
      `players.${action.playerId}.kingdomPool`,
      {
        laneId: action.laneId,
        cardDefinitionId: armyDefinition.id,
        ...resourceDetails(armyDefinition.cost, player.kingdomPool),
      },
    );
  }

  return { valid: true };
}
